import React, { useState } from 'react';
import { requestAllPermissions, AllPermissions, arePermissionsGranted } from '../../utils/permissions';
import './welcome-message.scss';

interface WelcomeMessageProps {
  onComplete: (permissions: AllPermissions) => void;
}

const WelcomeMessage: React.FC<WelcomeMessageProps> = ({ onComplete }) => {
  const [isRequestingPermissions, setIsRequestingPermissions] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [permissionError, setPermissionError] = useState<string | null>(null);

  const handleContinue = async () => {
    setIsRequestingPermissions(true);
    setPermissionError(null);

    try {
      const permissions = await requestAllPermissions();
      
      // Check if we got the essential permissions
      if (!arePermissionsGranted(permissions)) {
        // Show error but still allow continuation with limited functionality
        const errors = [];
        if (!permissions.microphone.granted) errors.push('microphone');
        if (!permissions.camera.granted) errors.push('camera');
        if (!permissions.location.granted) errors.push('location');
        
        setPermissionError(`Some permissions were denied: ${errors.join(', ')}. You can still use the app with limited functionality.`);
      }
      
      // Complete after a short delay to show any error message
      setTimeout(() => {
        onComplete(permissions);
      }, permissionError ? 2000 : 500);
      
    } catch (error) {
      setPermissionError('Failed to request permissions. You can still use the app with limited functionality.');
      setTimeout(() => {
        onComplete({
          location: { granted: false, error: 'Permission request failed' },
          microphone: { granted: false, error: 'Permission request failed' },
          camera: { granted: false, error: 'Permission request failed' }
        });
      }, 2000);
    } finally {
      setIsRequestingPermissions(false);
    }
  };

  const toggleDetails = () => {
    setShowDetails(!showDetails);
  };

  return (
    <div className="welcome-message">
      <div className="welcome-content">
        <div className="welcome-header">
          <h1>Welcome to Aqary Assistant!</h1>
          <p className="welcome-subtitle">
            Your AI-powered real estate companion
          </p>
        </div>

        <div className="features-overview">
          <h2>What I can help you with:</h2>
          <div className="features-grid">
            <div className="feature-item">
              <div className="feature-content">
                <h3>Property Evaluation</h3>
                <p>Get instant AI-powered property valuations</p>
              </div>
            </div>
            
            <div className="feature-item">
              <div className="feature-content">
                <h3>Visual Property Tours</h3>
                <p>Guided camera-based property analysis</p>
              </div>
            </div>
            
            <div className="feature-item">
              <div className="feature-content">
                <h3>Voice Interaction</h3>
                <p>Natural conversation with your AI assistant</p>
              </div>
            </div>
            
            <div className="feature-item">
              <div className="feature-content">
                <h3>Location Intelligence</h3>
                <p>Accurate pricing based on your location</p>
              </div>
            </div>
            
            <div className="feature-item">
              <div className="feature-content">
                <h3>Market Insights</h3>
                <p>Comprehensive analysis and PDF reports</p>
              </div>
            </div>
          </div>
        </div>

        <div className="permissions-section">
          <h3>To get started, we'll need access to:</h3>
          <div className="permissions-list">
            <div className="permission-item">
              <div className="permission-details">
                <strong>Location</strong>
                <span>For accurate property pricing</span>
              </div>
            </div>
            <div className="permission-item">
              <div className="permission-details">
                <strong>Microphone</strong>
                <span>For voice interaction</span>
              </div>
            </div>
            <div className="permission-item">
              <div className="permission-details">
                <strong>Camera</strong>
                <span>For property visual analysis</span>
              </div>
            </div>
          </div>
        </div>

        {showDetails && (
          <div className="privacy-details">
            <h4>Privacy & Security</h4>
            <ul>
              <li>Your location is only used for property evaluation and is not stored</li>
              <li>Camera feed is processed locally and not recorded</li>
              <li>Voice data is processed securely and not saved</li>
              <li>All data transmission is encrypted</li>
            </ul>
          </div>
        )}

        {permissionError && (
          <div className="permission-error">
            <p>{permissionError}</p>
          </div>
        )}

        <div className="welcome-actions">
          <button 
            className="btn-primary"
            onClick={handleContinue}
            disabled={isRequestingPermissions}
          >
            {isRequestingPermissions ? 'Requesting Permissions...' : 'Continue'}
          </button>
          
          <button 
            className="btn-secondary"
            onClick={toggleDetails}
          >
            {showDetails ? 'Hide Details' : 'Privacy Details'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WelcomeMessage;
